import type { PhraseScore } from './score-phrase.js'
import type { SungNote } from './types.js'

/** A reference note as the melody stores it: onset and duration in beats */
export interface ReferenceNote {
  midi: number
  onset: number
  duration: number
}

/** A rest this long ends a held note: the same as a phrase cut */
const restBreaksRunBeats = 1

/** Bowie, #2440: a held note covers a repeat when it lasts this share of the time out to the repeat's midpoint */
const heldCoverShare = 0.75

/**
 * Forgive the repeated notes a sustained note absorbed (Bowie, #2440 review and verdict).
 *
 * A pitch tracker hears a D held across three repeated Ds as one D, so the aligner pairs it with one
 * of them and calls the others missed. A missed reference note is forgiven when a paired member of its
 * run of same-pitch notes really held it:
 * - the paired note is the right pitch: a held wrong note is one mistake, and covers nothing;
 * - it lasts at least 75 % of the reference time from the paired note, counted outward, to the missed
 *   note's midpoint. A short D against D D D leaves two beats of silence, which is not a held note.
 * No re-attack is needed. A run breaks at a rest of a beat or more, since nobody holds a note through one.
 *
 * The reference stays unmerged, so a singer who articulates each D still pairs one for one and keeps
 * every D's timing; a forgiven note had no onset to time. Forgiven notes move from `missed` to
 * `forgiven`, so the phrase's length still reads right (Quincy, #2440).
 *
 * Only for capture through a microphone: a keyboard sends every repeated key, so MIDI stays strict.
 *
 * @param reference index-aligned with the `reference` the score was computed from
 * @param sung the notes the score was computed from
 * @param bpm the reference's tempo, to read its beats as seconds
 */
export const forgiveAbsorbedRepeats = (
  score: PhraseScore,
  reference: ReferenceNote[],
  sung: SungNote[],
  bpm: number
): PhraseScore => {
  const secondsPerBeat = 60 / bpm
  const missed = new Set(score.missed)
  /** Reference index → the paired sung note's length in seconds, for pairs of the right pitch */
  const held = new Map<number, number>()
  score.referenceIndices.forEach((referenceIndex, index) => {
    const note = sung[score.sungIndices[index]]
    if (note && score.absolute.perNote[index]?.band !== 'wrong-note') {
      held.set(referenceIndex, note.endTime - note.startTime)
    }
  })
  /** Whether notes `at` and `at + 1` are one held pitch: the same semitone, with no rest of a beat between */
  const isJoined = (at: number) => {
    const current = reference[at]
    const next = reference[at + 1]
    return (
      next !== undefined &&
      current.midi === next.midi &&
      next.onset - (current.onset + current.duration) < restBreaksRunBeats
    )
  }
  const midpoint = (note: ReferenceNote) => note.onset + note.duration / 2
  /** Whether the paired note at `from` was held out to the middle of the missed note at `to` */
  const covers = (from: number, to: number) => {
    const length = held.get(from)
    if (length === undefined) {
      return false
    }
    const paired = reference[from]
    const beats =
      to > from ? midpoint(reference[to]) - paired.onset : paired.onset + paired.duration - midpoint(reference[to])
    return length >= heldCoverShare * beats * secondsPerBeat
  }
  /** The nearest paired member of the run on each side; a missed member in between is not paired */
  const isAbsorbed = (index: number) => {
    for (let at = index - 1; at >= 0 && isJoined(at); at--) {
      if (!missed.has(at)) {
        if (covers(at, index)) {
          return true
        }
        break
      }
    }
    for (let at = index + 1; at < reference.length && isJoined(at - 1); at++) {
      if (!missed.has(at)) {
        return covers(at, index)
      }
    }
    return false
  }
  const forgiven = score.missed.filter(isAbsorbed)
  return {
    ...score,
    missed: score.missed.filter(index => !forgiven.includes(index)),
    forgiven: [...score.forgiven, ...forgiven].sort((a, b) => a - b),
  }
}
